import { Protocol } from '@kodiak-finance/router-sdk'
import { Currency, CurrencyAmount, Percent, Token, TradeType } from '@kodiak-finance/sdk-core'
import { FeeAmount } from '@kodiak-finance/v3-sdk'

import { RouteData, TokenInRoute, V2PoolInRoute, V3PoolInRoute } from './trades'

const parseToken = ({ address, chainId, decimals, symbol }: TokenInRoute): Token => {
  return new Token(chainId, address, parseInt(decimals.toString()), symbol)
}

function getRouteProtocol(route: (V3PoolInRoute | V2PoolInRoute)[]): Protocol {
  if (route.every((pool) => pool.type === 'v2-pool')) return Protocol.V2
  if (route.every((pool) => pool.type === 'v3-pool')) return Protocol.V3
  return Protocol.MIXED
}

const V2_DEFAULT_FEE_TIER = 3000

export interface RoutingDiagramEntry {
  percent: Percent
  path: [Currency, Currency, FeeAmount][]
  protocol: Protocol
}

export function getTokenPathFromQuote(
  currencyIn: Currency | undefined,
  currencyOut: Currency | undefined,
  tradeType: TradeType,
  quoteRoute: Array<RouteData[]> | undefined
): RoutingDiagramEntry[] {
  if (!quoteRoute || !currencyIn || !currencyOut) return []

  return quoteRoute.map((route) => {
    const rawAmountIn = route[0].amountIn as string
    const rawAmountOut = route[route.length - 1].amountOut as string

    const portion =
      tradeType === TradeType.EXACT_INPUT
        ? CurrencyAmount.fromRawAmount(currencyIn, rawAmountIn).divide(
            CurrencyAmount.fromRawAmount(currencyIn, rawAmountIn)
          )
        : CurrencyAmount.fromRawAmount(currencyOut, rawAmountOut).divide(
            CurrencyAmount.fromRawAmount(currencyOut, rawAmountOut)
          )

    const percent = new Percent(portion.numerator, portion.denominator)

    const path: RoutingDiagramEntry['path'] = []
    for (let i = 0; i < route.length; i++) {
      const nextPool = route[i]
      const tokenIn = parseToken(nextPool.tokenIn)
      const tokenOut = parseToken(nextPool.tokenOut)
      const entry: RoutingDiagramEntry['path'][0] = [
        tokenIn,
        tokenOut,
        nextPool.type === 'v3-pool' ? (parseInt((nextPool as V3PoolInRoute).fee) as FeeAmount) : V2_DEFAULT_FEE_TIER,
      ]
      path.push(entry)
    }

    return {
      percent,
      path,
      protocol: getRouteProtocol(route),
    }
  })
}

export function sanitizePriceImpact(priceImpact: string): Percent | null {
  if (priceImpact === 'NaN' || priceImpact === '') {
    return null
  }
  const numericValue = parseFloat(priceImpact)

  if (numericValue === Infinity) {
    return null
  }

  if (isNaN(numericValue)) {
    return null
  }

  if (numericValue === -Infinity) {
    return new Percent(-9999, 10000)
  }

  const cappedImpact = Math.sign(numericValue) * Math.min(Math.abs(numericValue), 99.99)
  const basisPoints = Math.floor(cappedImpact * 100)

  return new Percent(basisPoints, 10000)
}
